import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  GlobalMessageLevel,
  GlobalMessageTopic,
  type GlobalMessage,
} from '../models/GlobalMessage';
import type { CommissionStateResponse } from '../models/CommissioningDevice';
import type { PHASE } from '../utils/constants';

export const useGlobalMessageStore = defineStore('globalMessage', () => {
  const subscribedTopics = ref<GlobalMessageTopic[]>([]);
  const currentGlobalMessages = ref<GlobalMessage>({} as GlobalMessage);

  /**
   * Takes the API response, and push message to the store if there is any Phase error.
   * @param response - The API response
   * @returns void
   */
  const checkPhaseError = (respondedPhases: CommissionStateResponse['phases']) => {
    clearTopicMessage(GlobalMessageTopic.MISWIRED);

    const subscribedToMiswired = subscribedTopics.value.includes(GlobalMessageTopic.MISWIRED);
    if (!subscribedToMiswired || !respondedPhases) {
      return;
    }

    try {
      const miswiredPhases = (Object.keys(respondedPhases) as PHASE[]).filter(
        (phaseKey: PHASE) => respondedPhases[phaseKey].miswired,
      );

      if (miswiredPhases.length > 0) {
        const errorDetails = {
          level: GlobalMessageLevel.ERROR,
          msg: `We detected phase miswiring on ${miswiredPhases.join(', ')}.`,
        };
        currentGlobalMessages.value[GlobalMessageTopic.MISWIRED] = errorDetails;
      }

      return;
    } catch (error) {
      console.error('Error parsing payload', error);
    }
  };

  /**
   * Clears all topic messages when the user leaves the current page where the popup is displayed.
   * This prevents old popup messages from appearing again if the user navigates back.
   *
   * Rationale:
   * - Since `checkPhaseError` is invoked across multiple pages, it's more efficient to clear the topic
   *   here rather than handling it in every previous page’s `onMounted` hook.
   */
  const handlePopState = () => {
    clearAllTopicMessages();
  };

  /**
   * Init the list of topics that we can show popups for.
   * Also sets up listener for browser back button to clear messages.
   */
  const connect = () => {
    subscribedTopics.value = Object.values(GlobalMessageTopic);
    window.addEventListener('popstate', handlePopState);
  };

  /**
   * Clear the ability to show popups and remove browser navigation listener.
   */
  const disconnect = () => {
    subscribedTopics.value = [];
    window.removeEventListener('popstate', handlePopState);
  };

  /** Only clears it if topic exists. Shouldn't add the topic key if it not already exists. */
  const clearTopicMessage = (topic: GlobalMessageTopic) => {
    if (Object.keys(currentGlobalMessages.value).includes(topic)) {
      currentGlobalMessages.value = {
        ...currentGlobalMessages.value,
        [topic]: undefined,
      };
    }
  };

  /** Clears all topic messages. */
  const clearAllTopicMessages = () => {
    currentGlobalMessages.value = {} as GlobalMessage;
  };

  return {
    // States
    subscribedTopics,
    currentGlobalMessages,

    // Actions
    connect,
    disconnect,
    clearTopicMessage,

    // Actions - handle API response
    checkPhaseError,
  };
});
